package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun LandingPage(onNavigateToBooking: () -> Unit) {
    Div({
        style {
            property("font-family", "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
            property("overflow-x", "hidden")
            color(Color("#1a1a2e"))
        }
    }) {
        LandingHeader(onNavigateToBooking)
        HeroSection(onNavigateToBooking)
        ServicesSection()
        PricingSection(onNavigateToBooking)
        HowItWorksSection()
        AppDownloadSection()
        CoachSection()
        LocationSection()
        LandingFooter()
    }
}

// ---------- HEADER ----------

@Composable
private fun LandingHeader(onNavigateToBooking: () -> Unit) {
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            property("z-index", "1000")
            backgroundColor(Color("#1a1a2e"))
            property("box-shadow", "0 2px 20px rgba(0,0,0,0.3)")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                maxWidth(1200.px)
                property("margin", "0 auto")
                padding(16.px, 24.px)
            }
        }) {
            // Logo / Brand
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("gap", "12px")
                }
            }) {
                Span({
                    style {
                        fontSize(28.px)
                        fontWeight("800")
                        color(Color.white)
                    }
                }) { Text("Nutri") }
                Span({
                    style {
                        fontSize(28.px)
                        fontWeight("800")
                        color(Color("#00e676"))
                    }
                }) { Text("MX") }
            }

            // Nav links
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("gap", "32px")
                }
            }) {
                val navItems = listOf("Servicios", "Precios", "Proceso", "Ubicacion")
                navItems.forEach { item ->
                    A(href = "#${item.lowercase()}") {
                        Span({
                            style {
                                color(Color("rgba(255,255,255,0.8)"))
                                fontSize(14.px)
                                fontWeight("500")
                                property("transition", "color 0.2s")
                                property("cursor", "pointer")
                            }
                        }) { Text(item) }
                    }
                }
                Button({
                    style {
                        backgroundColor(Color("#00e676"))
                        color(Color("#1a1a2e"))
                        property("border", "none")
                        borderRadius(24.px)
                        padding(10.px, 24.px)
                        fontWeight("700")
                        fontSize(14.px)
                        property("cursor", "pointer")
                        property("transition", "transform 0.2s, box-shadow 0.2s")
                        property("box-shadow", "0 4px 15px rgba(0,230,118,0.3)")
                    }
                    onClick { onNavigateToBooking() }
                }) { Text("Agendar Cita") }
            }
        }
    }
}

// ---------- HERO ----------

@Composable
private fun HeroSection(onNavigateToBooking: () -> Unit) {
    Div({
        id("hero")
        style {
            property("background", "linear-gradient(135deg, #1a1a2e 0%, #283593 50%, #1a1a2e 100%)")
            paddingTop(120.px)
            paddingBottom(80.px)
            paddingLeft(24.px)
            paddingRight(24.px)
            textAlign("center")
            property("min-height", "90vh")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
        }
    }) {
        Div({
            style {
                maxWidth(900.px)
                property("margin", "0 auto")
            }
        }) {
            // Mascot
            Img(src = "manzi_happy.png", alt = "Manzi - mascota NutriMX") {
                style {
                    width(160.px)
                    height(160.px)
                    property("margin-bottom", "32px")
                    property("animation", "float 3s ease-in-out infinite")
                    property("filter", "drop-shadow(0 8px 24px rgba(0,230,118,0.3))")
                }
            }

            H1({
                style {
                    color(Color.white)
                    fontSize(52.px)
                    fontWeight("800")
                    property("line-height", "1.2")
                    property("margin-bottom", "20px")
                    property("letter-spacing", "-1px")
                }
            }) {
                Text("Tu camino hacia una ")
                Span({
                    style {
                        color(Color("#00e676"))
                    }
                }) { Text("vida saludable") }
                Text(" comienza aqui")
            }

            P({
                style {
                    color(Color("rgba(255,255,255,0.8)"))
                    fontSize(20.px)
                    property("line-height", "1.6")
                    maxWidth(600.px)
                    property("margin", "0 auto 40px")
                }
            }) {
                Text("Coaching nutricional personalizado en Puebla. Planes basados en evidencia, seguimiento con tecnologia y acompanamiento real.")
            }

            // CTA Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    property("gap", "16px")
                    property("flex-wrap", "wrap")
                }
            }) {
                Button({
                    style {
                        backgroundColor(Color("#00e676"))
                        color(Color("#1a1a2e"))
                        property("border", "none")
                        borderRadius(32.px)
                        padding(16.px, 40.px)
                        fontWeight("700")
                        fontSize(18.px)
                        property("cursor", "pointer")
                        property("transition", "transform 0.2s, box-shadow 0.2s")
                        property("box-shadow", "0 6px 25px rgba(0,230,118,0.4)")
                    }
                    onClick { onNavigateToBooking() }
                }) { Text("Agendar Consulta Inicial — $250 MXN") }

                Button({
                    style {
                        backgroundColor(Color("transparent"))
                        color(Color.white)
                        property("border", "2px solid rgba(255,255,255,0.4)")
                        borderRadius(32.px)
                        padding(16.px, 40.px)
                        fontWeight("600")
                        fontSize(18.px)
                        property("cursor", "pointer")
                        property("transition", "border-color 0.2s")
                    }
                    onClick {
                        kotlinx.browser.window.location.href = "#servicios"
                    }
                }) { Text("Ver Servicios") }
            }
        }
    }

    // Float animation keyframes via style tag
    Style {
        """
        @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-12px); }
        }
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap');

        .landing-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 12px 40px rgba(0,0,0,0.15) !important;
        }
        .pricing-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 16px 48px rgba(0,0,0,0.18) !important;
        }
        .pricing-card-featured {
            transform: scale(1.05);
            border: 2px solid #00e676 !important;
        }
        .pricing-card-featured:hover {
            transform: scale(1.05) translateY(-8px);
        }
        .step-circle {
            width: 64px; height: 64px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 28px; font-weight: 800; color: white;
            background: linear-gradient(135deg, #283593, #00e676);
            margin: 0 auto 16px;
        }
        .cta-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 30px rgba(0,230,118,0.5) !important;
        }
        .social-link:hover {
            background: rgba(255,255,255,0.15) !important;
        }
        @media (max-width: 768px) {
            .landing-grid-3 { grid-template-columns: 1fr !important; }
            .landing-grid-4 { grid-template-columns: 1fr 1fr !important; }
            .hero-title { font-size: 36px !important; }
            .pricing-card-featured { transform: scale(1) !important; }
        }
        @media (max-width: 480px) {
            .landing-grid-4 { grid-template-columns: 1fr !important; }
        }
        """.trimIndent()
    }
}

// ---------- SERVICES ----------

@Composable
private fun ServicesSection() {
    Div({
        id("servicios")
        style {
            backgroundColor(Color.white)
            padding(80.px, 24.px)
        }
    }) {
        Div({
            style {
                maxWidth(1100.px)
                property("margin", "0 auto")
                textAlign("center")
            }
        }) {
            SectionLabel("SERVICIOS")
            H2({
                style {
                    fontSize(40.px)
                    fontWeight("800")
                    color(Color("#1a1a2e"))
                    property("margin-bottom", "16px")
                }
            }) { Text("Nutricion basada en ciencia") }
            P({
                style {
                    color(Color("#666"))
                    fontSize(18.px)
                    maxWidth(600.px)
                    property("margin", "0 auto 48px")
                    property("line-height", "1.6")
                }
            }) { Text("Cada consulta se adapta a tu estilo de vida, condiciones de salud y objetivos personales.") }

            // Cards
            Div({
                classes("landing-grid-3")
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(3, 1fr)")
                    property("gap", "28px")
                    textAlign("left")
                }
            }) {
                ServiceCard(
                    icon = "\uD83C\uDF3F",
                    title = "Consulta Inicial",
                    description = "Evaluacion completa: historial clinico, composicion corporal con bascula inteligente, analisis de habitos, y diseno de tu plan nutricional personalizado.",
                    duration = "60 min",
                    price = "$250 MXN"
                )
                ServiceCard(
                    icon = "\uD83D\uDCC8",
                    title = "Consulta de Seguimiento",
                    description = "Revision de avances, ajuste de plan, resolucion de dudas, y motivacion continua. Tu progreso, medido con datos reales.",
                    duration = "30 min",
                    price = "$150 MXN"
                )
                ServiceCard(
                    icon = "\uD83D\uDCF1",
                    title = "Consulta Express Online",
                    description = "Sesion rapida por videollamada para resolver dudas puntuales, ajustar porciones, o revisar tu registro alimenticio.",
                    duration = "20 min",
                    price = "$100 MXN"
                )
            }
        }
    }
}

@Composable
private fun ServiceCard(icon: String, title: String, description: String, duration: String, price: String) {
    Div({
        classes("landing-card")
        style {
            backgroundColor(Color("#f8f9ff"))
            borderRadius(16.px)
            padding(32.px)
            property("transition", "transform 0.3s, box-shadow 0.3s")
            property("box-shadow", "0 4px 16px rgba(0,0,0,0.06)")
            property("cursor", "default")
        }
    }) {
        Span({
            style { fontSize(40.px); property("display", "block"); property("margin-bottom", "16px") }
        }) { Text(icon) }
        H3({
            style {
                fontSize(22.px)
                fontWeight("700")
                color(Color("#1a1a2e"))
                property("margin-bottom", "12px")
            }
        }) { Text(title) }
        P({
            style {
                color(Color("#555"))
                fontSize(15.px)
                property("line-height", "1.6")
                property("margin-bottom", "20px")
            }
        }) { Text(description) }
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                paddingTop(16.px)
                property("border-top", "1px solid #e0e0e0")
            }
        }) {
            Span({
                style { color(Color("#888")); fontSize(14.px) }
            }) { Text(duration) }
            Span({
                style {
                    color(Color("#283593"))
                    fontWeight("700")
                    fontSize(18.px)
                }
            }) { Text(price) }
        }
    }
}

// ---------- PRICING ----------

@Composable
private fun PricingSection(onNavigateToBooking: () -> Unit) {
    Div({
        id("precios")
        style {
            property("background", "linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)")
            padding(80.px, 24.px)
        }
    }) {
        Div({
            style {
                maxWidth(1200.px)
                property("margin", "0 auto")
                textAlign("center")
            }
        }) {
            SectionLabel("PRECIOS")
            H2({
                style {
                    fontSize(40.px)
                    fontWeight("800")
                    color(Color("#1a1a2e"))
                    property("margin-bottom", "16px")
                }
            }) { Text("Planes y precios transparentes") }
            P({
                style {
                    color(Color("#666"))
                    fontSize(18.px)
                    maxWidth(600.px)
                    property("margin", "0 auto 48px")
                    property("line-height", "1.6")
                }
            }) { Text("Sin costos ocultos. Elige la opcion que mejor se adapte a ti.") }

            // Pricing grid
            Div({
                classes("landing-grid-3")
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(3, 1fr)")
                    property("gap", "24px")
                    property("align-items", "start")
                }
            }) {
                PricingCard(
                    title = "Consulta Inicial",
                    price = "$250",
                    period = "MXN",
                    features = listOf("60 minutos", "Evaluacion completa", "Composicion corporal", "Plan personalizado", "Acceso a app NutriMX"),
                    featured = false,
                    onNavigateToBooking = onNavigateToBooking
                )
                PricingCard(
                    title = "Paquete Arranque",
                    price = "$450",
                    period = "MXN",
                    features = listOf("Consulta Inicial (60 min)", "2 Seguimientos (30 min c/u)", "Ahorro de \$100", "Soporte por chat", "Acceso a app NutriMX"),
                    featured = true,
                    badge = "Mas popular",
                    onNavigateToBooking = onNavigateToBooking
                )
                PricingCard(
                    title = "Paquete Completo",
                    price = "$1,200",
                    period = "MXN",
                    features = listOf("Consulta Inicial (60 min)", "Bascula inteligente incluida", "4 Seguimientos (30 min c/u)", "Ahorro de \$250", "Soporte prioritario", "Acceso completo a app NutriMX"),
                    featured = false,
                    badge = "Mejor valor",
                    onNavigateToBooking = onNavigateToBooking
                )
            }

            // Additional services
            Div({
                style {
                    property("margin-top", "48px")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    property("gap", "24px")
                    property("flex-wrap", "wrap")
                }
            }) {
                SmallPricingCard("Consulta de Seguimiento", "$150 MXN", "30 min")
                SmallPricingCard("Consulta Express Online", "$100 MXN", "20 min")
                SmallPricingCard("Interpretacion de Laboratorios", "$150 MXN", "Analisis detallado")
            }
        }
    }
}

@Composable
private fun PricingCard(
    title: String,
    price: String,
    period: String,
    features: List<String>,
    featured: Boolean,
    badge: String? = null,
    onNavigateToBooking: () -> Unit
) {
    Div({
        classes("pricing-card")
        if (featured) classes("pricing-card-featured")
        style {
            backgroundColor(Color.white)
            borderRadius(20.px)
            padding(36.px, 28.px)
            property("transition", "transform 0.3s, box-shadow 0.3s")
            property("box-shadow", "0 4px 20px rgba(0,0,0,0.08)")
            textAlign("center")
            property("position", "relative")
            if (!featured) property("border", "1px solid #eee")
        }
    }) {
        badge?.let {
            Div({
                style {
                    property("position", "absolute")
                    top((-14).px)
                    left(0.px)
                    right(0.px)
                    textAlign("center")
                }
            }) {
                Span({
                    style {
                        backgroundColor(if (featured) Color("#00e676") else Color("#f0a500"))
                        color(if (featured) Color("#1a1a2e") else Color.white)
                        padding(6.px, 20.px)
                        borderRadius(20.px)
                        fontSize(13.px)
                        fontWeight("700")
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.5px")
                    }
                }) { Text(it) }
            }
        }

        H3({
            style {
                fontSize(20.px)
                fontWeight("700")
                color(Color("#1a1a2e"))
                property("margin-bottom", "8px")
                property("margin-top", if (badge != null) "8px" else "0")
            }
        }) { Text(title) }

        Div({
            style { property("margin-bottom", "24px") }
        }) {
            Span({
                style {
                    fontSize(48.px)
                    fontWeight("800")
                    color(Color("#283593"))
                }
            }) { Text(price) }
            Span({
                style {
                    fontSize(16.px)
                    color(Color("#888"))
                    property("margin-left", "4px")
                }
            }) { Text(period) }
        }

        // Features list
        Div({
            style {
                textAlign("left")
                property("margin-bottom", "28px")
            }
        }) {
            features.forEach { feature ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("gap", "10px")
                        padding(8.px, 0.px)
                    }
                }) {
                    Span({
                        style { color(Color("#00e676")); fontSize(18.px) }
                    }) { Text("\u2713") }
                    Span({
                        style { color(Color("#444")); fontSize(15.px) }
                    }) { Text(feature) }
                }
            }
        }

        Button({
            classes("cta-btn")
            style {
                width(100.percent)
                backgroundColor(if (featured) Color("#00e676") else Color("#283593"))
                color(if (featured) Color("#1a1a2e") else Color.white)
                property("border", "none")
                borderRadius(12.px)
                padding(14.px, 24.px)
                fontWeight("700")
                fontSize(16.px)
                property("cursor", "pointer")
                property("transition", "transform 0.2s, box-shadow 0.2s")
            }
            onClick { onNavigateToBooking() }
        }) { Text("Agendar Cita") }
    }
}

@Composable
private fun SmallPricingCard(title: String, price: String, detail: String) {
    Div({
        classes("landing-card")
        style {
            backgroundColor(Color.white)
            borderRadius(12.px)
            padding(24.px, 32.px)
            property("box-shadow", "0 2px 12px rgba(0,0,0,0.06)")
            property("border", "1px solid #eee")
            property("transition", "transform 0.3s, box-shadow 0.3s")
            textAlign("center")
            property("min-width", "220px")
        }
    }) {
        H4({
            style {
                fontSize(16.px)
                fontWeight("700")
                color(Color("#1a1a2e"))
                property("margin-bottom", "8px")
            }
        }) { Text(title) }
        Span({
            style {
                fontSize(28.px)
                fontWeight("800")
                color(Color("#283593"))
                property("display", "block")
                property("margin-bottom", "4px")
            }
        }) { Text(price) }
        Span({
            style { color(Color("#888")); fontSize(14.px) }
        }) { Text(detail) }
    }
}

// ---------- HOW IT WORKS ----------

@Composable
private fun HowItWorksSection() {
    Div({
        id("proceso")
        style {
            backgroundColor(Color("#1a1a2e"))
            padding(80.px, 24.px)
        }
    }) {
        Div({
            style {
                maxWidth(1000.px)
                property("margin", "0 auto")
                textAlign("center")
            }
        }) {
            SectionLabel("PROCESO", light = true)
            H2({
                style {
                    fontSize(40.px)
                    fontWeight("800")
                    color(Color.white)
                    property("margin-bottom", "16px")
                }
            }) { Text("Como funciona") }
            P({
                style {
                    color(Color("rgba(255,255,255,0.7)"))
                    fontSize(18.px)
                    maxWidth(600.px)
                    property("margin", "0 auto 48px")
                }
            }) { Text("4 pasos simples para transformar tu alimentacion.") }

            Div({
                classes("landing-grid-4")
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(4, 1fr)")
                    property("gap", "32px")
                }
            }) {
                StepCard("1", "Agenda tu cita", "Elige el horario que te funcione, presencial o en linea.")
                StepCard("2", "Evaluacion inicial", "Analizamos tu composicion corporal, habitos e historial clinico.")
                StepCard("3", "Plan personalizado", "Recibe tu plan nutricional diseñado para tus objetivos.")
                StepCard("4", "Seguimiento continuo", "Monitorea tu progreso con la app NutriMX y consultas periodicas.")
            }
        }
    }
}

@Composable
private fun StepCard(number: String, title: String, description: String) {
    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({ classes("step-circle") }) { Text(number) }
        H3({
            style {
                color(Color.white)
                fontSize(18.px)
                fontWeight("700")
                property("margin-bottom", "8px")
            }
        }) { Text(title) }
        P({
            style {
                color(Color("rgba(255,255,255,0.6)"))
                fontSize(15.px)
                property("line-height", "1.5")
            }
        }) { Text(description) }
    }
}

// ---------- APP DOWNLOAD ----------

@Composable
private fun AppDownloadSection() {
    Div({
        style {
            property("background", "linear-gradient(135deg, #283593 0%, #1a1a2e 100%)")
            padding(80.px, 24.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                maxWidth(700.px)
                property("margin", "0 auto")
            }
        }) {
            SectionLabel("TECNOLOGIA", light = true)
            H2({
                style {
                    fontSize(40.px)
                    fontWeight("800")
                    color(Color.white)
                    property("margin-bottom", "16px")
                }
            }) { Text("Descarga la app NutriMX") }
            P({
                style {
                    color(Color("rgba(255,255,255,0.8)"))
                    fontSize(18.px)
                    property("line-height", "1.6")
                    property("margin-bottom", "16px")
                }
            }) { Text("Registra tus comidas, ejercicio, peso, estado de animo y mas. Conecta tu bascula inteligente por Bluetooth y lleva el control total de tu salud.") }
            P({
                style {
                    color(Color("rgba(255,255,255,0.5)"))
                    fontSize(16.px)
                    property("margin-bottom", "32px")
                    fontStyle("italic")
                }
            }) { Text("Proximamente en App Store y Google Play") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    property("gap", "16px")
                    property("flex-wrap", "wrap")
                }
            }) {
                StoreBadge("App Store", "iOS")
                StoreBadge("Google Play", "Android")
            }
        }
    }
}

@Composable
private fun StoreBadge(store: String, platform: String) {
    A(href = "#") {
        Div({
            style {
                backgroundColor(Color("rgba(255,255,255,0.1)"))
                property("border", "1px solid rgba(255,255,255,0.2)")
                borderRadius(12.px)
                padding(14.px, 28.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "12px")
                property("transition", "background 0.2s")
                property("cursor", "pointer")
            }
        }) {
            Div {
                Span({
                    style { color(Color("rgba(255,255,255,0.6)")); fontSize(12.px); property("display", "block") }
                }) { Text("Disponible en") }
                Span({
                    style { color(Color.white); fontSize(18.px); fontWeight("700") }
                }) { Text(store) }
            }
        }
    }
}

// ---------- COACH ----------

@Composable
private fun CoachSection() {
    Div({
        style {
            backgroundColor(Color.white)
            padding(80.px, 24.px)
        }
    }) {
        Div({
            style {
                maxWidth(900.px)
                property("margin", "0 auto")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "60px")
                property("flex-wrap", "wrap")
                justifyContent(JustifyContent.Center)
            }
        }) {
            // Avatar placeholder
            Div({
                style {
                    width(200.px)
                    height(200.px)
                    borderRadius(50.percent)
                    property("background", "linear-gradient(135deg, #283593, #00e676)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("flex-shrink", "0")
                    property("box-shadow", "0 8px 32px rgba(40,53,147,0.3)")
                }
            }) {
                Span({
                    style { fontSize(64.px); color(Color.white); fontWeight("800") }
                }) { Text("LM") }
            }

            Div({
                style {
                    property("flex", "1")
                    property("min-width", "280px")
                }
            }) {
                SectionLabel("TU COACH")
                H2({
                    style {
                        fontSize(36.px)
                        fontWeight("800")
                        color(Color("#1a1a2e"))
                        property("margin-bottom", "16px")
                    }
                }) { Text("Luis Mendez") }
                P({
                    style {
                        color(Color("#283593"))
                        fontSize(18.px)
                        fontWeight("600")
                        property("margin-bottom", "16px")
                    }
                }) { Text("Coach Nutricional — Puebla, Puebla") }
                P({
                    style {
                        color(Color("#555"))
                        fontSize(16.px)
                        property("line-height", "1.7")
                        property("margin-bottom", "16px")
                    }
                }) {
                    Text("Apasionado por la nutricion basada en evidencia y la tecnologia aplicada a la salud. Mi enfoque combina ciencia, empatia y herramientas digitales para ayudarte a alcanzar tus objetivos de forma sostenible.")
                }
                P({
                    style {
                        color(Color("#555"))
                        fontSize(16.px)
                        property("line-height", "1.7")
                    }
                }) {
                    Text("Creador de NutriMX, la app que conecta tu bascula inteligente, registra tus habitos y te da el seguimiento que mereces.")
                }
            }
        }
    }
}

// ---------- LOCATION ----------

@Composable
private fun LocationSection() {
    Div({
        id("ubicacion")
        style {
            backgroundColor(Color("#f8f9ff"))
            padding(80.px, 24.px)
        }
    }) {
        Div({
            style {
                maxWidth(900.px)
                property("margin", "0 auto")
                textAlign("center")
            }
        }) {
            SectionLabel("UBICACION")
            H2({
                style {
                    fontSize(40.px)
                    fontWeight("800")
                    color(Color("#1a1a2e"))
                    property("margin-bottom", "16px")
                }
            }) { Text("Visitanos o conectate en linea") }
            P({
                style {
                    color(Color("#666"))
                    fontSize(18.px)
                    property("margin-bottom", "48px")
                }
            }) { Text("Consultas presenciales y por videollamada.") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    property("gap", "32px")
                    property("flex-wrap", "wrap")
                }
            }) {
                // In-person card
                Div({
                    classes("landing-card")
                    style {
                        backgroundColor(Color.white)
                        borderRadius(16.px)
                        padding(36.px)
                        property("box-shadow", "0 4px 16px rgba(0,0,0,0.06)")
                        property("transition", "transform 0.3s, box-shadow 0.3s")
                        property("flex", "1")
                        property("min-width", "280px")
                        property("max-width", "400px")
                        textAlign("left")
                    }
                }) {
                    Span({
                        style { fontSize(36.px); property("display", "block"); property("margin-bottom", "16px") }
                    }) { Text("\uD83D\uDCCD") }
                    H3({
                        style {
                            fontSize(20.px)
                            fontWeight("700")
                            color(Color("#1a1a2e"))
                            property("margin-bottom", "12px")
                        }
                    }) { Text("Consultorio") }
                    P({
                        style {
                            color(Color("#555"))
                            fontSize(15.px)
                            property("line-height", "1.6")
                        }
                    }) {
                        Text("Cerrada Pascual Orozco 1-7")
                        Br()
                        Text("Col. Pino Suarez, 72020")
                        Br()
                        Text("Puebla, Puebla")
                    }
                }

                // Online card
                Div({
                    classes("landing-card")
                    style {
                        backgroundColor(Color.white)
                        borderRadius(16.px)
                        padding(36.px)
                        property("box-shadow", "0 4px 16px rgba(0,0,0,0.06)")
                        property("transition", "transform 0.3s, box-shadow 0.3s")
                        property("flex", "1")
                        property("min-width", "280px")
                        property("max-width", "400px")
                        textAlign("left")
                    }
                }) {
                    Span({
                        style { fontSize(36.px); property("display", "block"); property("margin-bottom", "16px") }
                    }) { Text("\uD83D\uDCBB") }
                    H3({
                        style {
                            fontSize(20.px)
                            fontWeight("700")
                            color(Color("#1a1a2e"))
                            property("margin-bottom", "12px")
                        }
                    }) { Text("En linea") }
                    P({
                        style {
                            color(Color("#555"))
                            fontSize(15.px)
                            property("line-height", "1.6")
                        }
                    }) {
                        Text("Consultas por videollamada")
                        Br()
                        Text("desde cualquier lugar de Mexico.")
                        Br()
                        Text("Ideal para seguimientos rapidos.")
                    }
                }
            }
        }
    }
}

// ---------- FOOTER ----------

@Composable
private fun LandingFooter() {
    Div({
        style {
            backgroundColor(Color("#1a1a2e"))
            padding(48.px, 24.px)
        }
    }) {
        Div({
            style {
                maxWidth(1100.px)
                property("margin", "0 auto")
            }
        }) {
            // Top row
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexStart)
                    property("flex-wrap", "wrap")
                    property("gap", "32px")
                    property("margin-bottom", "40px")
                }
            }) {
                // Brand
                Div {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("gap", "8px")
                            property("margin-bottom", "12px")
                        }
                    }) {
                        Span({ style { fontSize(24.px); fontWeight("800"); color(Color.white) } }) { Text("Nutri") }
                        Span({ style { fontSize(24.px); fontWeight("800"); color(Color("#00e676")) } }) { Text("MX") }
                    }
                    P({
                        style {
                            color(Color("rgba(255,255,255,0.5)"))
                            fontSize(14.px)
                            maxWidth(280.px)
                            property("line-height", "1.5")
                        }
                    }) { Text("Coaching nutricional personalizado con tecnologia. Puebla, Mexico.") }
                }

                // Social links
                Div {
                    H4({
                        style {
                            color(Color.white)
                            fontSize(14.px)
                            fontWeight("700")
                            property("margin-bottom", "16px")
                            property("text-transform", "uppercase")
                            property("letter-spacing", "1px")
                        }
                    }) { Text("Siguenos") }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            property("gap", "12px")
                        }
                    }) {
                        SocialLink("Instagram", "https://www.instagram.com/nutrimxapp/")
                        SocialLink("TikTok", "https://www.tiktok.com/@nutrimxapp")
                        SocialLink("YouTube", "https://www.youtube.com/@NutriMX")
                    }
                }

                // Quick links
                Div {
                    H4({
                        style {
                            color(Color.white)
                            fontSize(14.px)
                            fontWeight("700")
                            property("margin-bottom", "16px")
                            property("text-transform", "uppercase")
                            property("letter-spacing", "1px")
                        }
                    }) { Text("Enlaces") }
                    val links = listOf("Servicios" to "#servicios", "Precios" to "#precios", "Proceso" to "#proceso", "Ubicacion" to "#ubicacion")
                    links.forEach { (label, href) ->
                        A(href = href) {
                            P({
                                style {
                                    color(Color("rgba(255,255,255,0.6)"))
                                    fontSize(14.px)
                                    property("margin-bottom", "8px")
                                    property("transition", "color 0.2s")
                                    property("cursor", "pointer")
                                }
                            }) { Text(label) }
                        }
                    }
                }
            }

            // Divider
            Div({
                style {
                    property("border-top", "1px solid rgba(255,255,255,0.1)")
                    paddingTop(24.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    property("flex-wrap", "wrap")
                    property("gap", "12px")
                }
            }) {
                P({
                    style {
                        color(Color("rgba(255,255,255,0.4)"))
                        fontSize(13.px)
                    }
                }) { Text("2024-2026 NutriMX. Todos los derechos reservados.") }
                P({
                    style {
                        color(Color("rgba(255,255,255,0.4)"))
                        fontSize(13.px)
                    }
                }) { Text("Puebla, Puebla, Mexico") }
            }
        }
    }
}

@Composable
private fun SocialLink(name: String, url: String) {
    A(href = url, attrs = {
        attr("target", "_blank")
        attr("rel", "noopener noreferrer")
    }) {
        Div({
            classes("social-link")
            style {
                backgroundColor(Color("rgba(255,255,255,0.08)"))
                borderRadius(8.px)
                padding(10.px, 16.px)
                property("transition", "background 0.2s")
                property("cursor", "pointer")
            }
        }) {
            Span({
                style { color(Color.white); fontSize(14.px); fontWeight("500") }
            }) { Text(name) }
        }
    }
}

// ---------- HELPERS ----------

@Composable
private fun SectionLabel(text: String, light: Boolean = false) {
    Span({
        style {
            color(if (light) Color("#00e676") else Color("#283593"))
            fontSize(13.px)
            fontWeight("700")
            property("letter-spacing", "2px")
            property("text-transform", "uppercase")
            property("display", "block")
            property("margin-bottom", "12px")
        }
    }) { Text(text) }
}
